import {read, WorkBook} from 'xlsx';
import {CreateSafeDigReply, ProjectReply, SafeDigImport, UpdateSafeDigReply} from '../Api/SafeDigTypes';
import {CreateSafeDigRequest} from '../Api/SafeDigsClients';
import {SafeDigClient} from '../Api/SafeDigsClients';
import {utils} from 'xlsx';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';

class ExcelImport {
    private m_fileData?: WorkBook;
    private m_errors: string[];
    private m_selectedSheet: number;
    private m_projectList: string[];
    private m_safedigClient?: NSwagAdapter<SafeDigClient>;

    constructor() {
        this.m_selectedSheet = -1;
        this.m_errors = [];
        this.m_projectList = [];
    }

    get SelectedSheet(): number {
        return this.m_selectedSheet;
    }

    set SelectedSheet(newValue: number) {
        this.m_selectedSheet = newValue;
    }

    get ErrorMessages(): string[] {
        return this.m_errors;
    }

    public ReadFile(fileRef: File, readComplete: (worksheetNames: string[]) => void): void {
        this.m_errors = [];
        const reader = new FileReader();
        reader.readAsArrayBuffer(fileRef);

        reader.onload = (e: any) => {
            this.m_fileData = read(e?.target.result, {type: 'binary', cellDates: true});

            //return a list of worksheets within the file.
            readComplete(this.m_fileData.SheetNames);
        };
    }

    public async ImportEntries(safeDigClient: NSwagAdapter<SafeDigClient>, userName: string): Promise<number> {
        this.m_safedigClient = safeDigClient;

        await this.FillProjectList();

        let importCount = 0;
        let index = 0;
        this.m_errors = [];

        if(this.m_fileData !== null) {
            const sheetName = this.m_fileData!.SheetNames[this.m_selectedSheet];

            for(const digEntry of utils.sheet_to_json<SafeDigImport>(this.m_fileData!.Sheets[sheetName])) {
                index++;
                if(this.CheckData(digEntry, index + 1)) {
                    if(!(await this.CheckForDuplicate(safeDigClient, digEntry.ID!, index + 1))) {
                        const projectValue = this.GetProjectValue(digEntry.Project);
                        const createRequest = new CreateSafeDigRequest({
                            estimate: digEntry.ID,
                            project: this.GetProjectValue(digEntry.Project),
                            requestor: digEntry.Requestor,
                            address1: digEntry.Address1,
                            address2: digEntry.Address2,
                            address3: digEntry.Address3,
                            county: digEntry.BuildArea,
                            postcode: digEntry.Postcode,
                            workDescription: digEntry.WorkDescription,
                            northing: digEntry.Northing,
                            easting: digEntry.Easting,
                            northing2: digEntry.Northing2,
                            easting2: digEntry.Easting2,
                            requiredDate: digEntry.RequiredDate!,
                            user: userName,
                            parent: -1
                        });
                        if(projectValue > 0) {
                            const createReply = await safeDigClient.callWithReply(CreateSafeDigReply, (client) => client.post(createRequest));

                            createReply.throwIfFailed();

                            importCount++;
                        } else {
                            this.m_errors.push(`Line number ${index} has invalid project.`);
                        }
                    }
                }
            }
        }

        return importCount;
    }

    private CheckData(digEntry: any, lineNumber: number): boolean {
        const objectKeys = Object.keys(digEntry);
        const expectedKeys = [
            'ID',
            'Project',
            'Requestor',
            'Address1',
            'BuildArea',
            'Postcode',
            'WorkDescription',
            'RequiredDate',
            'Northing',
            'Northing2',
            'Easting',
            'Easting2'
        ];

        const missingKeys = expectedKeys.filter((name) => !objectKeys.includes(name));

        if(missingKeys.length !== 0) {
            let errorMsg = `Line number ${lineNumber} missing columns: `;
            for(let index = 0; index < missingKeys.length; index++) {
                errorMsg += '\'' + missingKeys[index] + '\'' + (index < (missingKeys.length - 1) ? ', ' : '.');
            }
            this.m_errors.push(errorMsg);
        }

        let dataValid = true;
        for(const checkKey of expectedKeys) {
            if(digEntry[checkKey] === undefined) {
                this.m_errors.push(`Line number ${lineNumber} has no value for column '${checkKey}'.`);
                dataValid = false;
            }
        }
        return dataValid;
    }

    private async FillProjectList() {
        const projectResult = await this.m_safedigClient!.callWithReply(
            ProjectReply, (client) => client.getProjects());

        projectResult.throwIfFailed();

        this.m_projectList = projectResult!.reply!.data.map(project => (
            project.name.trim().toLowerCase()
        ));
    }

    private GetProjectValue(projectName: string): number {
        return this.m_projectList.indexOf(projectName.trim().toLowerCase()) + 1;
    }

    private async CheckForDuplicate(safeDigClient: NSwagAdapter<SafeDigClient>, estimate: string, lineNumber: number): Promise<boolean> {
        const countResult = await safeDigClient.callWithReply(UpdateSafeDigReply,
            (client) => client.count(-1, estimate));

        countResult.throwIfFailed();

        const duplicate = countResult.reply!.value !== 0;
        if(duplicate) {
            this.m_errors.push(`Estimate ${estimate} on line ${lineNumber} already exists.`);
        }

        return duplicate;
    }
}

export default ExcelImport;